import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Loader2, Plus, X, Tags, Tag, FolderKanban, Globe } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { tasksApi, projectsApi, keywordGroupsApi, type Project, type AnalysisAccountProfile, type KeywordGroup } from "@/api";
import PipelineOptions, { getDefaultStages } from "@/components/PipelineOptions";

export default function BatchScraper() {
  const queryClient = useQueryClient();

  // Form
  const [batchUrls, setBatchUrls] = useState("");
  const [contentKeyword, setContentKeyword] = useState("");
  const [priority, setPriority] = useState("normal");

  // Project
  const [projectId, setProjectId] = useState("__none__");
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectAccount, setNewProjectAccount] = useState("__system_default__");

  // Group & Tags
  const [groupId, setGroupId] = useState("__none__");
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [newTagInput, setNewTagInput] = useState("");
  const [pipelineStages, setPipelineStages] = useState<string[]>(getDefaultStages('batch'));

  // Data
  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ["projects"],
    queryFn: async () => { const r = await projectsApi.list(); return r.data || []; },
  });

  const { data: accountProfiles = [] } = useQuery<AnalysisAccountProfile[]>({
    queryKey: ["analysis-account-profiles"],
    queryFn: async () => (await projectsApi.analysisAccountProfiles()).data || [],
  });

  const pid = projectId !== "__none__" ? projectId : undefined;

  const { data: groups = [] } = useQuery<KeywordGroup[]>({
    queryKey: ["keyword-groups", pid],
    queryFn: async () => pid ? (await keywordGroupsApi.list(pid)).data || [] : [],
    enabled: !!pid,
  });

  const { data: projectTags = [] } = useQuery<string[]>({
    queryKey: ["project-tags", pid],
    queryFn: async () => pid ? (await keywordGroupsApi.allTags(pid)).data || [] : [],
    enabled: !!pid,
  });

  // Mutations
  const createProjectMutation = useMutation({
    mutationFn: () => projectsApi.create({
      name: newProjectName.trim(),
      analysis_account_profile_key: newProjectAccount === "__system_default__" ? null : newProjectAccount as "personal" | "company",
    }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      const id = res.data?.project_id;
      if (id) setProjectId(id);
      toast.success(`專案「${newProjectName.trim()}」已建立`);
      setNewProjectName(""); setNewProjectAccount("__system_default__"); setShowNewProject(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const createGroupMutation = useMutation({
    mutationFn: () => keywordGroupsApi.create({ project_id: projectId, name: newGroupName.trim() }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["keyword-groups", pid] });
      const id = res.data?.group_id;
      if (id) setGroupId(id);
      toast.success(`群組「${newGroupName.trim()}」已建立`);
      setNewGroupName(""); setShowNewGroup(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const batchCreateMutation = useMutation({
    mutationFn: (urls: string[]) => tasksApi.createBatch({
      urls,
      keyword: contentKeyword.trim() || undefined,
      project_id: pid,
      priority: priority === "high" ? 1 : undefined,
      selected_stages: pipelineStages.length > 0 ? pipelineStages : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      const urls = batchUrls.split("\n").map(u => u.trim()).filter(Boolean);
      toast.success(`已提交 ${urls.length} 個網址解析任務`);
      // Save keyword to group if configured
      if (groupId !== "__none__" && contentKeyword.trim()) {
        keywordGroupsApi.addItems(groupId, {
          keywords: [contentKeyword.trim()],
          tags: selectedTags.length ? selectedTags : undefined,
        }).catch(() => {});
      }
      setBatchUrls("");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleSubmit = () => {
    const urls = batchUrls.split("\n").map(u => u.trim()).filter(Boolean);
    if (urls.length === 0) { toast.error("請輸入至少一個網址"); return; }
    if (urls.length > 200) { toast.error("最多 200 個網址"); return; }
    batchCreateMutation.mutate(urls);
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const addNewTag = () => {
    const tag = newTagInput.trim();
    if (tag && !selectedTags.includes(tag)) setSelectedTags(prev => [...prev, tag]);
    setNewTagInput("");
  };

  const urlCount = batchUrls.split("\n").filter(u => u.trim()).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Globe className="h-6 w-6" />批量網址解析
        </h1>
        <p className="text-muted-foreground">手動輸入網址批量爬取解析（阿里巴巴、B2B 平台等）</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />網址輸入</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* URL List */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>網址清單 *</Label>
              <span className="text-xs text-muted-foreground">{urlCount} 個網址（最多 200 個）</span>
            </div>
            <Textarea rows={8} className="font-mono text-sm"
              placeholder={"每行一個網址，例如：\nhttps://www.alibaba.com/product-detail/xxx.html\nhttps://www.alibaba.com/product-detail/yyy.html\nhttps://www.made-in-china.com/product/zzz.html"}
              value={batchUrls} onChange={e => setBatchUrls(e.target.value)} />
          </div>

          {/* Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>優先級</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">一般 — 依序處理</SelectItem>
                  <SelectItem value="high">高優先 — 立即執行</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>目標關鍵字（選填）（共用）</Label>
              <Input placeholder="例如：isolation gowns" value={contentKeyword} onChange={e => setContentKeyword(e.target.value)} />
            </div>
          </div>

          {/* Project selector */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5"><FolderKanban className="h-3.5 w-3.5" />專案</Label>
              <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs"
                onClick={() => { setShowNewProject(!showNewProject); }}>
                {showNewProject ? "選擇現有" : "+ 建立新專案"}
              </Button>
            </div>
            {showNewProject ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input placeholder="新專案名稱" value={newProjectName} onChange={e => setNewProjectName(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && newProjectName.trim()) createProjectMutation.mutate(); }} />
                  <Button size="sm" disabled={!newProjectName.trim() || createProjectMutation.isPending}
                    onClick={() => createProjectMutation.mutate()}>
                    {createProjectMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "建立"}
                  </Button>
                </div>
                <Select value={newProjectAccount} onValueChange={setNewProjectAccount}>
                  <SelectTrigger><SelectValue placeholder="分析帳戶" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__system_default__">系統預設</SelectItem>
                    {accountProfiles.map(profile => (
                      <SelectItem key={profile.accountKey} value={profile.accountKey} disabled={!profile.dataforseoConfigured || !profile.llmConfigured}>
                        {profile.accountKey === "personal" ? "Personal（DataForSEO + OpenAI）" : "Company（DataForSEO + CometAPI）"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <Select value={projectId} onValueChange={v => { setProjectId(v); setGroupId("__none__"); }}>
                <SelectTrigger><SelectValue placeholder="不指定專案" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">不指定專案</SelectItem>
                  {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Tags row — auto-populated with keyword */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1"><Tag className="h-3 w-3" />Tags 標籤</Label>
            <div className="flex flex-wrap items-center gap-2 p-2 rounded-md border bg-muted/30 min-h-[36px]">
              {contentKeyword.trim() && (
                <Badge variant="default" className="text-xs gap-1">
                  {contentKeyword.trim()}
                  <span className="text-[10px] text-muted-foreground/70 ml-0.5">自動</span>
                </Badge>
              )}
              {selectedTags.filter(t => t !== contentKeyword.trim()).map(tag => (
                <Badge key={tag} variant="outline" className="text-xs gap-1">
                  {tag}
                  <button onClick={() => setSelectedTags(prev => prev.filter(t => t !== tag))} className="ml-0.5 hover:text-destructive">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <Input
                className="h-7 w-[140px] border-none bg-transparent text-xs p-0 px-1 focus-visible:ring-0"
                placeholder="+ 新增 Tag..."
                value={newTagInput}
                onChange={e => setNewTagInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && newTagInput.trim()) {
                    e.preventDefault();
                    addNewTag();
                  }
                }}
              />
            </div>
            <p className="text-xs text-muted-foreground">搜尋關鍵字會自動成為 Tag，搜尋完成後所有任務自動掛上這些 Tags</p>
          </div>

          {/* Group & Tags (only when project selected) */}
          {pid && (
            <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs flex items-center gap-1.5"><Tags className="h-3.5 w-3.5" />關鍵字群組（選填）</Label>
                  <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs"
                    onClick={() => setShowNewGroup(!showNewGroup)}>
                    {showNewGroup ? "選擇現有" : "+ 新增群組"}
                  </Button>
                </div>
                {showNewGroup ? (
                  <div className="flex gap-2">
                    <Input className="h-8 text-sm" placeholder="新群組名稱" value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter" && newGroupName.trim()) createGroupMutation.mutate(); }} />
                    <Button size="sm" className="h-8" disabled={!newGroupName.trim() || createGroupMutation.isPending}
                      onClick={() => createGroupMutation.mutate()}>
                      {createGroupMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "建立"}
                    </Button>
                  </div>
                ) : (
                  <Select value={groupId} onValueChange={setGroupId}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="不存入群組" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">不存入群組</SelectItem>
                      {groups.map(g => (
                        <SelectItem key={g.group_id} value={g.group_id}>{g.name} ({g.keyword_count} 個)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {groupId !== "__none__" && (
                <div className="space-y-2">
                  <Label className="text-xs">Tags</Label>
                  {selectedTags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {selectedTags.map(tag => (
                        <Badge key={tag} variant="default" className="text-xs gap-1 pr-1">
                          {tag}
                          <button onClick={() => toggleTag(tag)} className="ml-0.5 hover:text-destructive"><X className="h-3 w-3" /></button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  {projectTags.filter(t => !selectedTags.includes(t)).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-[11px] text-muted-foreground">已有 Tag：</span>
                      {projectTags.filter(t => !selectedTags.includes(t)).map(tag => (
                        <Badge key={tag} variant="outline" className="text-xs cursor-pointer hover:bg-primary/10"
                          onClick={() => toggleTag(tag)}>{tag}</Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-1.5">
                    <Input className="h-8 text-sm flex-1" placeholder="輸入新 Tag..."
                      value={newTagInput} onChange={e => setNewTagInput(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addNewTag(); } }} />
                    <Button size="sm" variant="outline" className="h-8" onClick={addNewTag} disabled={!newTagInput.trim()}>
                      <Plus className="h-3 w-3 mr-1" />加入
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pipeline Stages */}
          <PipelineOptions page="batch" selectedStages={pipelineStages} onChange={setPipelineStages} />

          {/* Submit */}
          <Button onClick={handleSubmit} className="w-full" size="lg"
            disabled={batchCreateMutation.isPending || urlCount === 0}>
            {batchCreateMutation.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />提交中...</>
            ) : (
              <><FileText className="h-4 w-4 mr-2" />開始批量解析 ({urlCount} 個網址)</>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
